import { fromPromise, setup } from "xstate-5";
import { InjectionKey } from "vue";
import { useMachine } from "@xstate-5/vue";
import { xlog } from "vue-app/utils/xstate";
import { AxiosError } from "axios";
import { storeToRefs } from "pinia";
import { useFreemiumStore, Customers } from "./Freemium.store";
import { DTOCreateSubscriptionResponse, DTOFreemiumLimits, DTOTierPrice } from "vue-app/api/dto";
import { getFreemium, postFreemiumUpgrade, getFreemiumPricingTiers } from "vue-app/api/freemium/freemium.ts";
import { COUNTRY_ID } from "vue-app/types/enums.ts";
import { showIntercomNewMessage } from "vue-app/utils/functions.ts";

interface ActorParams<T> {
  input: T;
}

export type AssasForm = {
  name: string;
  cpf_cnpj: string;
  email: string;
  company?: string;
  phone_prefix: string;
  phone_number: string;
  postal_code: string;
  street: string;
  street_number: string;
  province: string;
  complement?: string;
  simei?: boolean;
  simples_nacional?: boolean;
};

export const initializeFreemiumMachine = () => {
  const store = useFreemiumStore();
  const {
    customersCount,
    recommendedCustomersCount,
    daysToReset,
    usedMessageCredits,
    paying,
    priceTiers,
    selectedContacts,
    selectedCustomersOption,
    limitMessageCredits,
    loaded,
  } = storeToRefs(store);

  const setDefaultCustomersOption = () => {
    if (recommendedCustomersCount.value <= 1) {
      selectedCustomersOption.value = Customers.All;
    } else {
      selectedCustomersOption.value = Customers.Recommended;
    }
  };

  const updateSelectedContactsForOption = () => {
    if (selectedCustomersOption.value === Customers.All) {
      selectedContacts.value = customersCount.value;
    } else if (selectedCustomersOption.value === Customers.Recommended) {
      selectedContacts.value = recommendedCustomersCount.value;
    }
  };

  return setup({
    types: {
      tags: {} as
        | "fetchingData"
        | "error"
        | "paying"
        | "notPaying"
        | "fetchingFreemium"
        | "waiting"
        | "idle"
        | "default"
        | "upgradeDrawer"
        | "manageContacts"
        | "priceTiersDrawer"
        | "upgradingPlan"
        | "assasForm",
      events: {} as
        | { type: "UPDATE_FREEMIUM" }
        | { type: "UPGRADE_PLAN" }
        | { type: "MANAGE_CONTACTS" }
        | { type: "SELECT_CONTACTS"; contacts: number }
        | { type: "SELECT_IMPORT_OPTION"; option: Customers }
        | { type: "OPEN_TIERS" }
        | { type: "CANCEL" }
        | { type: "CONFIRM" }
        | { type: "CONTACT" }
        | { type: "CLOSE" }
        | { type: "BACK" }
        | { type: "SUBMIT_ASSAS_FORM"; form: AssasForm }
        | {
            type: "xstate.done.actor.fetchData";
            output: { freemium: DTOFreemiumLimits; priceTiers: DTOTierPrice[] };
          }
        | { type: "xstate.error.actor.fetchData"; error: AxiosError }
        | {
            type: "xstate.done.actor.fetchFreemium";
            output: DTOFreemiumLimits;
          }
        | { type: "xstate.error.actor.fetchFreemium"; error: AxiosError }
        | {
            type: "xstate.done.actor.upgradePlan";
            output: DTOCreateSubscriptionResponse;
          }
        | { type: "xstate.error.actor.upgradePlan"; error: AxiosError },
    },
    actors: {
      fetchData: fromPromise(async () => {
        xlog({ label: "fetchData" });
        const [freemium, priceTiers] = await Promise.all([getFreemium(), getFreemiumPricingTiers()]);
        return {
          freemium,
          priceTiers,
        };
      }),
      fetchFreemium: fromPromise(async () => {
        xlog({ label: "fetchFreemium" });
        return getFreemium();
      }),
      upgradePlan: fromPromise(
        async ({
          input,
        }: ActorParams<{ customers: number; form?: AssasForm; hygienizationType?: string } | undefined>) => {
          xlog({ label: "upgradePlan" });
          if (input) {
            const isAllContacts = selectedCustomersOption.value === Customers.All;
            return await postFreemiumUpgrade(input.customers, isAllContacts, input.form, input.hygienizationType);
          }
        }
      ),
    },
    guards: {
      isNotBrazil: () => {
        return window.EDRONE_CONTEXT.tracker.countryId !== COUNTRY_ID.BRAZIL;
      },
    },
  }).createMachine({
    id: "machineFreemium",
    initial: "fetchingData",
    states: {
      fetchingData: {
        tags: "fetchingData",
        invoke: {
          src: "fetchData",
          id: "fetchData",
          onDone: [
            {
              target: "paying",
              actions: ({ event }) => {
                priceTiers.value = event.output.priceTiers || [];
                customersCount.value = event.output.freemium.customersCount;
                recommendedCustomersCount.value = event.output.freemium.recommendedCustomersCount;
                limitMessageCredits.value = event.output.freemium.messageLimit;
                usedMessageCredits.value = event.output.freemium.messageCredits;
                daysToReset.value = event.output.freemium.daysToReset;
                paying.value = event.output.freemium.paying;
                loaded.value = true;
                setDefaultCustomersOption();
                updateSelectedContactsForOption();
              },
              guard: ({ event }) => {
                return event.output.freemium.paying;
              },
            },
            {
              target: "notPaying",
              actions: ({ event }) => {
                priceTiers.value = event.output.priceTiers || [];
                customersCount.value = event.output.freemium.customersCount;
                recommendedCustomersCount.value = event.output.freemium.recommendedCustomersCount;
                limitMessageCredits.value = event.output.freemium.messageLimit;
                usedMessageCredits.value = event.output.freemium.messageCredits;
                daysToReset.value = event.output.freemium.daysToReset;
                paying.value = event.output.freemium.paying;
                loaded.value = true;
                setDefaultCustomersOption();
                updateSelectedContactsForOption();
              },
            },
          ],
          onError: {
            target: "error",
          },
        },
      },

      notPaying: {
        type: "parallel",
        states: {
          fetching: {
            initial: "waiting",
            states: {
              fetchingFreemium: {
                tags: "fetchingFreemium",
                invoke: {
                  src: "fetchFreemium",
                  id: "fetchFreemium",
                  onDone: [
                    {
                      target: "#paying",
                      guard: ({ event }) => {
                        return event.output.paying;
                      },
                    },
                    {
                      target: "waiting",
                      actions: ({ event }) => {
                        customersCount.value = event.output.customersCount;
                        recommendedCustomersCount.value = event.output.recommendedCustomersCount;
                        limitMessageCredits.value = event.output.messageLimit;
                        usedMessageCredits.value = event.output.messageCredits;
                        daysToReset.value = event.output.daysToReset;
                        paying.value = event.output.paying;
                        loaded.value = true;
                        setDefaultCustomersOption();
                        updateSelectedContactsForOption();
                      },
                    },
                  ],
                  onError: [
                    {
                      target: "waiting",
                    },
                  ],
                },
              },
              waiting: {
                after: {
                  60000: {
                    // 1 * 60 * 1000 = 1 minute
                    target: "fetchingFreemium",
                  },
                },
                on: {
                  UPDATE_FREEMIUM: {
                    target: "fetchingFreemium",
                  },
                },
              },
            },
          },
          idle: {
            initial: "default",
            states: {
              default: {
                id: "default",
                on: {
                  UPGRADE_PLAN: {
                    target: "upgradeDrawer",
                    actions: updateSelectedContactsForOption,
                  },
                },
              },

              upgradeDrawer: {
                tags: "upgradeDrawer",
                id: "upgradeDrawer",
                initial: "idle",
                states: {
                  idle: {
                    on: {
                      SELECT_IMPORT_OPTION: {
                        actions: ({ event }) => {
                          selectedCustomersOption.value = event.option;
                          updateSelectedContactsForOption();
                        },
                      },
                      MANAGE_CONTACTS: {
                        target: "#manageContacts",
                        actions: () => {
                          selectedCustomersOption.value = Customers.Manual;
                        },
                      },
                      OPEN_TIERS: "priceTiersDrawer",
                      CONFIRM: [{ target: "upgradingPlan", guard: "isNotBrazil" }, { target: "assasForm" }],
                      CANCEL: "#default",
                      CONTACT: {
                        actions: () => {
                          showIntercomNewMessage(window._trans._freemium.contactNotification.message);
                        },
                      },
                    },
                  },

                  priceTiersDrawer: {
                    tags: "priceTiersDrawer",
                    on: {
                      CLOSE: {
                        target: "idle",
                      },
                      CANCEL: {
                        target: "#default",
                      },
                    },
                  },

                  assasForm: {
                    tags: "assasForm",
                    on: {
                      BACK: {
                        target: "idle",
                      },
                      SUBMIT_ASSAS_FORM: {
                        target: "upgradingPlan",
                      },
                    },
                  },

                  upgradingPlan: {
                    tags: "upgradingPlan",
                    invoke: {
                      src: "upgradePlan",
                      id: "upgradePlan",
                      input: ({ event }) => {
                        return {
                          customers: selectedContacts.value,
                          form: event.type === "SUBMIT_ASSAS_FORM" ? event.form : undefined,
                          hygienizationType:
                            selectedCustomersOption.value === Customers.Recommended ? "RECOMMENDED" : undefined,
                        };
                      },
                      onDone: [
                        {
                          target: "#default",
                          actions: ({ event }) => {
                            window.showToast(window._trans._freemium.toasts.success.upgrade, "success");
                            if (event.output?.paymentLink) {
                              window.open(event.output.paymentLink, "_self");
                            }
                            localStorage.removeItem("freemiumUpgradeAssasForm");
                          },
                        },
                      ],
                      onError: [
                        {
                          target: "idle",
                          actions: () => {
                            window.showToast(window._trans._freemium.toasts.error.upgrade, "error");
                          },
                          guard: "isNotBrazil",
                        },
                        {
                          target: "assasForm",
                          actions: () => {
                            window.showToast(window._trans._freemium.toasts.error.upgrade, "error");
                          },
                        },
                      ],
                    },
                  },
                },
              },

              manageContacts: {
                tags: "manageContacts",
                id: "manageContacts",
                initial: "idle",
                states: {
                  idle: {
                    on: {
                      SELECT_IMPORT_OPTION: {
                        target: "#upgradeDrawer",
                        actions: ({ event }) => {
                          selectedCustomersOption.value = event.option;
                          updateSelectedContactsForOption();
                        },
                      },
                      OPEN_TIERS: "priceTiersDrawer",
                      SELECT_CONTACTS: {
                        actions: ({ event }) => {
                          selectedContacts.value = event.contacts;
                        },
                      },
                      CONFIRM: [{ target: "upgradingPlan", guard: "isNotBrazil" }, { target: "assasForm" }],
                      CANCEL: "#upgradeDrawer",
                      CONTACT: {
                        actions: () => {
                          showIntercomNewMessage(window._trans._freemium.contactNotification.message);
                        },
                      },
                    },
                  },
                  priceTiersDrawer: {
                    tags: "priceTiersDrawer",
                    on: {
                      CLOSE: {
                        target: "idle",
                      },
                      CANCEL: {
                        target: "#default",
                      },
                    },
                  },

                  assasForm: {
                    tags: "assasForm",
                    on: {
                      BACK: {
                        target: "idle",
                      },
                      SUBMIT_ASSAS_FORM: {
                        target: "upgradingPlan",
                      },
                    },
                  },

                  upgradingPlan: {
                    tags: "upgradingPlan",
                    invoke: {
                      src: "upgradePlan",
                      id: "upgradePlan",
                      input: ({ event }) => {
                        return {
                          customers: selectedContacts.value,
                          form: event.type === "SUBMIT_ASSAS_FORM" ? event.form : undefined,
                          hygienizationType:
                            selectedCustomersOption.value === Customers.Recommended ? "RECOMMENDED" : undefined,
                        };
                      },
                      onDone: [
                        {
                          target: "#default",
                          actions: ({ event }) => {
                            window.showToast(window._trans._freemium.toasts.success.upgrade, "success");
                            if (event.output?.paymentLink) {
                              window.open(event.output.paymentLink, "_self");
                            }
                            localStorage.removeItem("freemiumUpgradeAssasForm");
                          },
                        },
                      ],
                      onError: [
                        {
                          target: "idle",
                          actions: () => {
                            window.showToast(window._trans._freemium.toasts.error.upgrade, "error");
                          },
                          guard: "isNotBrazil",
                        },
                        {
                          target: "assasForm",
                          actions: () => {
                            window.showToast(window._trans._freemium.toasts.error.upgrade, "error");
                          },
                        },
                      ],
                    },
                  },
                },
              },
            },
          },
        },
      },

      paying: {
        type: "final",
        id: "paying",
      },

      error: {
        after: {
          10000: {
            target: "fetchingData",
          },
        },
      },
    },
  });
};

export const symbolMachineFreemium: InjectionKey<
  ReturnType<typeof useMachine<ReturnType<typeof initializeFreemiumMachine>>>
> = Symbol();
